import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Space, Divider, Pagination } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Page } from 'osep-react-antd';
import useProjectPresetsSearchQuery from '../hooks/query/useProjectPresetsSearchQuery';
import useCli from '../hooks/query/useCli';
import ErrorPage from './ErrorPage';
import { organizeParamsByFeatureGroup } from '../utils/organizeParamsByFeatureGroup';
import ProjectPresetsInput from '../components/ProjectPresetsCollection';
import { ITEMS_PER_PAGE } from '../utils/consts';
import ProjectPresetsCards from '../components/presets/ProjectPresetsCards';
import ProjectPresetsFilter from '../components/presets/ProjectPresetsFilter';

const AddProjectPresets: React.FC = () => {
  const { data: cliData, isLoading: cliLoading, isError: cliError } = useCli();
  const navigate = useNavigate();

  const [activeFilterType, setActiveFilterType] = useState<string | undefined>(
    '',
  );
  const [searchText, setSearchText] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const queryParams = useMemo(
    () => (activeFilterType ? { type: [activeFilterType] } : {}),
    [activeFilterType],
  );
  const { data: projectPresets = [], isLoading: presetsLoading } =
    useProjectPresetsSearchQuery(queryParams);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);

  const filteredPresets = useMemo(() => {
    return projectPresets.filter((project) => {
      const matchesType = activeFilterType
        ? project.type.toLowerCase() === activeFilterType.toLowerCase()
        : true;

      const matchesSearchText =
        project.name.toLowerCase().includes(searchText.trim().toLowerCase()) ||
        project.params.some(
          (param) =>
            param.value
              ?.toLowerCase()
              .includes(searchText.trim().toLowerCase()),
        );

      return matchesType && matchesSearchText;
    });
  }, [projectPresets, activeFilterType, searchText]);

  const calculateRowHeights = useCallback(() => {
    const rows: number[][] = [];
    let currentRow: number[] = [];

    cardRefs.current.forEach((ref, index) => {
      if (ref) {
        currentRow.push(ref.clientHeight);
        if ((index + 1) % 4 === 0) {
          rows.push(currentRow);
          currentRow = [];
        }
      }
    });

    if (currentRow.length > 0) {
      rows.push(currentRow);
    }

    const newRowHeights = rows.map((row) => Math.max(...row));
    setRowHeights(newRowHeights);
  }, []);

  useEffect(() => {
    if (!presetsLoading) {
      calculateRowHeights();
    }
  }, [presetsLoading, filteredPresets, calculateRowHeights]);

  useEffect(() => {
    const observer = new ResizeObserver(calculateRowHeights);
    cardRefs.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, [calculateRowHeights, projectPresets]);

  const featureGroups = useMemo(
    () => organizeParamsByFeatureGroup(cliData),
    [cliData],
  );

  if (cliError) {
    return <ErrorPage error={cliError} />;
  }

  return (
    <Page title="Presets" onBack={() => navigate(-1)} isLoading={cliLoading}>
      <Space direction="vertical" size="large" style={{ display: 'flex' }}>
        <h2>Add Presets</h2>
        <ProjectPresetsInput featureGroups={featureGroups} />
      </Space>

      <Divider />

      <ProjectPresetsFilter
        featureGroups={featureGroups}
        activeFilterType={activeFilterType}
        setActiveFilterType={setActiveFilterType}
        searchText={searchText}
        setSearchText={setSearchText}
        setCurrentPage={setCurrentPage}
      />

      <ProjectPresetsCards
        presetsLoading={presetsLoading}
        filteredPresets={filteredPresets}
        currentPage={currentPage}
        rowHeights={rowHeights}
        featureGroups={featureGroups}
        cardRefs={cardRefs}
      />

      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}
      >
        <Pagination
          current={currentPage}
          total={filteredPresets.length}
          pageSize={ITEMS_PER_PAGE}
          onChange={(page) => setCurrentPage(page)}
        />
      </div>
    </Page>
  );
};

export default AddProjectPresets;
